import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';
import { inputBase } from './Input.js';

const MAX_MENU_HEIGHT = 288;
const GAP = 6;
const MARGIN = 8;

const sizes = {
  sm: 'h-8 px-2.5 text-sm',
  md: 'h-9 px-3',
  lg: 'h-10 px-3.5',
};

/**
 * Custom select for long option lists. The menu is portaled to `body`, matches
 * the trigger width and opens upwards when there is not enough room below
 * (a native <select> near the bottom of the page shows a clipped list).
 *
 * options: Array<{ value, label, description?, disabled? }>
 * onChange(value)
 */
export default function SelectMenu({ id, value, onChange, options = [], disabled = false, invalid = false, size = 'md', placeholder = 'Select…', className }) {
  const buttonRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, width: 0, maxHeight: MAX_MENU_HEIGHT });
  const selected = options.find((option) => option.value === value);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - GAP - MARGIN;
    const openUp = spaceBelow < MAX_MENU_HEIGHT && spaceAbove > spaceBelow;
    setPos({
      top: openUp ? undefined : rect.bottom + GAP,
      bottom: openUp ? window.innerHeight - rect.top + GAP : undefined,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(MAX_MENU_HEIGHT, openUp ? spaceAbove : spaceBelow),
      openUp,
    });
  }, []);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={cn('relative', className)}>
          <Listbox.Button
            ref={buttonRef}
            id={id}
            onClick={updatePosition}
            className={cn(
              inputBase,
              sizes[size] || sizes.md,
              'flex items-center pr-9 text-left',
              open && 'ring-2 ring-indigo-500',
              invalid && 'ring-red-300 focus:ring-red-500',
            )}
            aria-invalid={invalid || undefined}
          >
            <span className={cn('block truncate', !selected && 'text-gray-400')}>{selected ? selected.label : placeholder}</span>
            <ChevronDownIcon
              className={cn('pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform', open && 'rotate-180')}
              aria-hidden="true"
            />
          </Listbox.Button>
          <PortalOptions open={open} pos={pos} updatePosition={updatePosition} options={options} />
        </div>
      )}
    </Listbox>
  );
}

function PortalOptions({ open, pos, updatePosition, options }) {
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Transition
      as={Fragment}
      show={open}
      enter="transition ease-out duration-100"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <Listbox.Options
        static
        style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
        className={cn(
          'z-[90] overflow-y-auto rounded-xl bg-white p-1 shadow-pop ring-1 ring-gray-900/5 focus:outline-none',
          pos.openUp ? 'origin-bottom' : 'origin-top',
        )}
      >
        {options.map((option) => (
          <Listbox.Option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={({ active, disabled }) =>
              cn(
                'flex cursor-pointer select-none items-center gap-2 rounded-lg py-2 pl-2.5 pr-2 text-sm',
                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                disabled && 'cursor-not-allowed opacity-50',
              )
            }
          >
            {({ selected }) => (
              <>
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate', selected && 'font-medium text-gray-900')}>{option.label}</span>
                  {option.description && <span className="block truncate text-xs text-gray-500">{option.description}</span>}
                </span>
                {selected && <CheckIcon className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />}
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Transition>,
    document.body,
  );
}
